<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  PRIORITY_LABELS,
  SOURCE_LABELS,
  STATUS_LABELS,
  STATUS_TAG,
  assignTask,
  autoDispatch,
  canAct,
  cancelTask,
  createTask,
  listTasks,
  type AutoResult,
  type Task,
  type TaskInput,
  type TaskRow,
} from '../api/dispatch'
import { listDriverOptions, type Driver } from '../api/drivers'
import { listRouteOptions, type Route } from '../api/routes'
import {
  STATUS_LABELS as TRIP_STATUS_LABELS,
  type TripStatus,
  createTrip,
  listPlannedTrips,
  listTrips,
  type TripRow,
} from '../api/trips'
import { STATUS_LABELS as VEHICLE_STATUS_LABELS, listVehicleOptions, type VehicleRow } from '../api/vehicles'

const rows = ref<TaskRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const refError = ref<unknown>(null)
const notice = ref('')

const query = reactive({ keyword: '', status: '', priority: '', page: 1, size: 20 })

const formOpen = ref(false)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive({
  source_type: 'manual' as Task['source_type'],
  source_no: '',
  cargo_desc: '',
  weight_kg: 0,
  volume_cm3: 0,
  pickup_addr: '',
  deliver_addr: '',
  priority: 'normal' as Task['priority'],
})

const assignOpen = ref(false)
const assignTarget = ref<TaskRow | null>(null)
const assignSaving = ref(false)
const assignError = ref<unknown>(null)
const assignForm = reactive({
  mode: 'trip' as 'trip' | 'vehicle',
  trip_id: undefined as number | undefined,
  vehicle_id: undefined as number | undefined,
  route_id: undefined as number | undefined,
  driver_id: undefined as number | undefined,
})

const plannedTrips = ref<TripRow[]>([])
const vehicles = ref<VehicleRow[]>([])
const routes = ref<Route[]>([])
const drivers = ref<Driver[]>([])

const autoOpen = ref(false)
const autoBusy = ref(false)
const autoResult = ref<AutoResult | null>(null)

const sourceNoError = computed(() =>
  form.source_type === 'manual' || form.source_no.trim() ? '' : '引用外部单号时必须填写单号',
)
const cargoError = computed(() => (form.cargo_desc.trim() ? '' : '请填写货物描述'))
const createErrorText = computed(() => sourceNoError.value || cargoError.value)

const assignErrorText = computed(() => {
  if (assignForm.mode === 'trip') return assignForm.trip_id ? '' : '请选择行车任务'
  return assignForm.vehicle_id ? '' : '请选择车辆'
})

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listTasks({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      status: query.status || undefined,
      priority: query.priority || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => void load())

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function openCreate(): void {
  Object.assign(form, {
    source_type: 'manual' as Task['source_type'],
    source_no: '',
    cargo_desc: '',
    weight_kg: 0,
    volume_cm3: 0,
    pickup_addr: '',
    deliver_addr: '',
    priority: 'normal' as Task['priority'],
  })
  formError.value = null
  formOpen.value = true
}

async function save(): Promise<void> {
  if (createErrorText.value) return
  saving.value = true
  formError.value = null
  const input: TaskInput = {
    source_type: form.source_type,
    source_no: form.source_no.trim() || undefined,
    cargo_desc: form.cargo_desc.trim(),
    weight_kg: Number(form.weight_kg) || 0,
    volume_cm3: Number(form.volume_cm3) || 0,
    pickup_addr: form.pickup_addr.trim() || undefined,
    deliver_addr: form.deliver_addr.trim() || undefined,
    priority: form.priority,
  }
  try {
    const created = await createTask(input)
    formOpen.value = false
    await load(`任务 ${created.task_no} 已创建`)
  } catch (e) {
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function loadAssignRefs(): Promise<void> {
  refError.value = null
  try {
    const [trips, vehs, rts, drs] = await Promise.all([
      listPlannedTrips(),
      listVehicleOptions(),
      listRouteOptions(),
      listDriverOptions(),
    ])
    plannedTrips.value = trips
    vehicles.value = vehs
    routes.value = rts
    drivers.value = drs
  } catch (e) {
    // 参照数据拿不到派车弹窗会空，但必须把原因露出来
    refError.value = e
  }
}

function openAssign(row: TaskRow): void {
  assignTarget.value = row
  Object.assign(assignForm, {
    mode: 'trip' as 'trip' | 'vehicle',
    trip_id: undefined,
    vehicle_id: undefined,
    route_id: undefined,
    driver_id: undefined,
  })
  assignError.value = null
  assignOpen.value = true
  void loadAssignRefs()
}

/** 换车时带上这台车绑定的司机，避免每次都要手选 */
function onVehiclePicked(): void {
  const v = vehicles.value.find((item) => item.id === assignForm.vehicle_id)
  if (v?.driver_id) assignForm.driver_id = v.driver_id
}

async function submitAssign(): Promise<void> {
  if (!assignTarget.value || assignErrorText.value) return
  assignSaving.value = true
  assignError.value = null
  try {
    let tripId = assignForm.trip_id
    if (assignForm.mode === 'vehicle') {
      // 后端没有「按车辆派车」的接口，这里按客户端两步走：该车已有计划中的行车任务就复用，
      // 没有就先建一张 planned 行车任务再挂单（POST /trips 允许线路与司机留空）。
      const found = await listTrips({
        page: 1,
        size: 1,
        status: 'planned',
        vehicle_id: assignForm.vehicle_id,
      })
      const reuse = found.list[0]
      if (reuse) {
        tripId = reuse.id
      } else {
        const created = await createTrip({
          vehicle_id: assignForm.vehicle_id ?? 0,
          route_id: assignForm.route_id,
          driver_id: assignForm.driver_id,
        })
        tripId = created.id
      }
    }
    if (!tripId) return
    const row = await assignTask(assignTarget.value.id, tripId)
    assignOpen.value = false
    await load(`任务 ${row.task_no} 已派至 ${row.trip_doc_no || `行车任务 #${tripId}`}`)
  } catch (e) {
    // 容量超限 / 状态不符都是 409，原样把 trace_id 露出来
    assignError.value = e
  } finally {
    assignSaving.value = false
  }
}

async function cancelRow(row: TaskRow): Promise<void> {
  notice.value = ''
  refError.value = null
  try {
    await cancelTask(row.id)
    await load(`任务 ${row.task_no} 已取消`)
  } catch (e) {
    refError.value = e
  }
}

async function runAuto(): Promise<void> {
  autoBusy.value = true
  refError.value = null
  autoResult.value = null
  try {
    const out = await autoDispatch()
    autoResult.value = out
    autoOpen.value = true
    await load(`智能派车完成：派上 ${out.assigned.length} 单，剩余 ${out.unassigned.length} 单`)
  } catch (e) {
    refError.value = e
  } finally {
    autoBusy.value = false
  }
}
</script>

<template>
  <div class="yc-dsp">
    <header class="yc-dsp__bar">
      <el-input
        v-model="query.keyword"
        class="yc-dsp__keyword"
        placeholder="任务号 / 外部单号 / 货描"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.status" class="yc-dsp__ref" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-select v-model="query.priority" class="yc-dsp__ref" placeholder="全部优先级" clearable @change="search">
        <el-option v-for="(label, value) in PRIORITY_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'dsp:task:create'" type="primary" @click="openCreate">新建任务</el-button>
      <el-popconfirm
        title="将按「加急优先、体积重量大的优先」把待调度任务装进空闲且装得下的车辆，是否继续？"
        width="360"
        @confirm="runAuto"
      >
        <template #reference>
          <el-button v-perm="'dsp:task:auto'" type="success" :loading="autoBusy">一键智能派车</el-button>
        </template>
      </el-popconfirm>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-dsp__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-dsp__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-dsp__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="task_no" label="任务号" min-width="150" />
        <el-table-column label="来源" min-width="170" show-overflow-tooltip>
          <template #default="{ row }">
            <el-tag size="small" type="info">{{ SOURCE_LABELS[row.source_type as Task['source_type']] ?? row.source_type }}</el-tag>
            {{ row.source_no || '—' }}
          </template>
        </el-table-column>
        <el-table-column prop="cargo_desc" label="货描" min-width="140" show-overflow-tooltip />
        <el-table-column label="提货 → 送达" min-width="220" show-overflow-tooltip>
          <template #default="{ row }">{{ row.pickup_addr || '—' }} → {{ row.deliver_addr || '—' }}</template>
        </el-table-column>
        <el-table-column label="重量 / 体积（kg / cm³）" width="200" align="right">
          <template #default="{ row }">
            {{ Number(row.weight_kg).toFixed(3) }} / {{ Number(row.volume_cm3).toFixed(2) }}
          </template>
        </el-table-column>
        <el-table-column label="优先级" width="90">
          <template #default="{ row }">
            <el-tag :type="row.priority === 'urgent' ? 'danger' : 'info'" size="small">
              {{ PRIORITY_LABELS[row.priority as Task['priority']] ?? row.priority }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as Task['status']] ?? 'info'" size="small">
              {{ STATUS_LABELS[row.status as Task['status']] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="行车任务" min-width="160" show-overflow-tooltip>
          <template #default="{ row }">
            {{ row.trip_doc_no ? `${row.trip_doc_no}（${row.plate_no || '未绑车'}）` : '未派车' }}
          </template>
        </el-table-column>
        <el-table-column label="创建时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="160" fixed="right">
          <template #default="{ row }">
            <el-button
              v-if="canAct(row.status, 'assign')"
              v-perm="'dsp:task:assign'"
              link
              type="primary"
              @click="openAssign(row)"
            >
              派车
            </el-button>
            <el-popconfirm
              v-if="canAct(row.status, 'cancel')"
              title="确认取消该调度任务？已派车的任务会从行车任务上摘除。"
              width="300"
              @confirm="cancelRow(row)"
            >
              <template #reference>
                <el-button v-perm="'dsp:task:cancel'" link type="danger">取消</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="没有待调度任务"
        description="手工新建任务，或引用 ops 订单号 / wms 出库单号；建好后点「一键智能派车」。"
      >
        <el-button v-perm="'dsp:task:create'" type="primary" @click="openCreate">新建任务</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-dsp__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog v-if="formOpen" v-model="formOpen" title="新建调度任务" width="620px" :close-on-click-modal="false">
      <div v-if="formError" class="yc-dsp__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <div class="yc-dsp__formrow">
          <el-form-item label="任务来源">
            <el-select v-model="form.source_type" class="yc-dsp__full">
              <el-option v-for="(label, value) in SOURCE_LABELS" :key="value" :label="label" :value="value" />
            </el-select>
          </el-form-item>

          <el-form-item label="外部单号" :error="sourceNoError">
            <el-input
              v-model="form.source_no"
              maxlength="64"
              :placeholder="form.source_type === 'manual' ? '可留空' : '必填，如 DD202610050001'"
            />
          </el-form-item>
        </div>

        <el-form-item label="货物描述" :error="cargoError">
          <el-input v-model="form.cargo_desc" maxlength="255" placeholder="如 日用百货 10 箱" />
        </el-form-item>

        <div class="yc-dsp__formrow">
          <el-form-item label="重量（kg）">
            <el-input-number
              v-model="form.weight_kg"
              :min="0"
              :max="999999"
              :precision="3"
              controls-position="right"
            />
          </el-form-item>

          <el-form-item label="体积（cm³）">
            <el-input-number
              v-model="form.volume_cm3"
              :min="0"
              :max="9999999999"
              :precision="2"
              controls-position="right"
            />
          </el-form-item>

          <el-form-item label="优先级">
            <el-radio-group v-model="form.priority">
              <el-radio value="normal">普通</el-radio>
              <el-radio value="urgent">加急</el-radio>
            </el-radio-group>
          </el-form-item>
        </div>

        <div class="yc-dsp__formrow">
          <el-form-item label="提货地址">
            <el-input v-model="form.pickup_addr" maxlength="255" placeholder="可留空" />
          </el-form-item>

          <el-form-item label="送达地址">
            <el-input v-model="form.deliver_addr" maxlength="255" placeholder="可留空" />
          </el-form-item>
        </div>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!createErrorText">
          创建
        </el-button>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-if="assignOpen"
      v-model="assignOpen"
      :title="`派车 · ${assignTarget?.task_no ?? ''}`"
      width="560px"
      :close-on-click-modal="false"
    >
      <div v-if="assignError" class="yc-dsp__error">
        <TraceIdAlert :error="assignError" />
      </div>

      <el-form label-position="top" @submit.prevent="submitAssign">
        <div v-if="assignTarget" class="yc-dsp__facts">
          <div class="yc-dsp__fact"><span>货描</span>{{ assignTarget.cargo_desc || '—' }}</div>
          <div class="yc-dsp__fact"><span>重量 / 体积</span>{{ Number(assignTarget.weight_kg).toFixed(3) }} kg / {{ Number(assignTarget.volume_cm3).toFixed(2) }} cm³</div>
          <div class="yc-dsp__fact"><span>提货 → 送达</span>{{ assignTarget.pickup_addr || '—' }} → {{ assignTarget.deliver_addr || '—' }}</div>
        </div>

        <el-form-item label="派车方式">
          <el-radio-group v-model="assignForm.mode">
            <el-radio value="trip">选择已有行车任务</el-radio>
            <el-radio value="vehicle">选择车辆</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item v-if="assignForm.mode === 'trip'" label="行车任务" :error="assignErrorText">
          <el-select v-model="assignForm.trip_id" class="yc-dsp__full" filterable placeholder="选择计划中的行车任务">
            <el-option
              v-for="t in plannedTrips"
              :key="t.id"
              :label="`${t.doc_no} · ${t.plate_no || '未绑车'} · 已载 ${t.task_count} 单`"
              :value="t.id"
            />
          </el-select>
          <p v-if="!plannedTrips.length" class="yc-dsp__hint">
            暂无「计划中」的行车任务，切到「选择车辆」会先给车建一张再挂单。
          </p>
        </el-form-item>

        <template v-else>
          <el-form-item label="车辆" :error="assignErrorText">
            <el-select
              v-model="assignForm.vehicle_id"
              class="yc-dsp__full"
              filterable
              placeholder="选择出车的车辆"
              @change="onVehiclePicked"
            >
              <el-option
                v-for="v in vehicles"
                :key="v.id"
                :label="`${v.plate_no}（${VEHICLE_STATUS_LABELS[v.status] ?? v.status}）`"
                :value="v.id"
              />
            </el-select>
          </el-form-item>

          <div class="yc-dsp__formrow">
            <el-form-item label="线路">
              <el-select v-model="assignForm.route_id" class="yc-dsp__full" clearable filterable placeholder="可留空">
                <el-option
                  v-for="r in routes"
                  :key="r.id"
                  :label="`${r.code} ${r.name}`.trim()"
                  :value="r.id"
                />
              </el-select>
            </el-form-item>

            <el-form-item label="司机">
              <el-select v-model="assignForm.driver_id" class="yc-dsp__full" clearable filterable placeholder="可留空">
                <el-option
                  v-for="d in drivers"
                  :key="d.id"
                  :label="`${d.code} ${d.name}`.trim()"
                  :value="d.id"
                />
              </el-select>
            </el-form-item>
          </div>

          <p class="yc-dsp__hint">该车已有「计划中」的行车任务就直接挂上，没有会自动新建一张再挂。</p>
        </template>

        <el-button type="primary" native-type="submit" :loading="assignSaving" :disabled="!!assignErrorText">
          确认派车
        </el-button>
      </el-form>

      <template #footer>
        <el-button @click="assignOpen = false">取消</el-button>
      </template>
    </el-dialog>

    <el-dialog v-if="autoOpen" v-model="autoOpen" title="一键智能派车结果" width="900px">
      <template v-if="autoResult">
        <p class="yc-dsp__summary">
          派上 <b>{{ autoResult.assigned.length }}</b> 单，未派上 <b>{{ autoResult.unassigned.length }}</b> 单，
          用到 <b>{{ autoResult.trips.length }}</b> 张行车任务。
        </p>

        <template v-if="autoResult.assigned.length">
          <h3 class="yc-dsp__sect">已派车（{{ autoResult.assigned.length }}）</h3>
          <el-table :data="autoResult.assigned" row-key="id">
            <el-table-column prop="task_no" label="任务号" min-width="150" />
            <el-table-column prop="plate_no" label="车牌" min-width="120" />
            <el-table-column prop="trip_doc_no" label="行车任务号" min-width="150" />
            <el-table-column label="重量（kg）" width="120" align="right">
              <template #default="{ row }">{{ Number(row.weight_kg).toFixed(3) }}</template>
            </el-table-column>
            <el-table-column label="体积（cm³）" width="130" align="right">
              <template #default="{ row }">{{ Number(row.volume_cm3).toFixed(2) }}</template>
            </el-table-column>
          </el-table>
        </template>

        <template v-if="autoResult.unassigned.length">
          <h3 class="yc-dsp__sect">未派上（{{ autoResult.unassigned.length }}，留在待调度）</h3>
          <el-table :data="autoResult.unassigned" row-key="id">
            <el-table-column prop="task_no" label="任务号" min-width="150" />
            <el-table-column label="优先级" width="90">
              <template #default="{ row }">{{ PRIORITY_LABELS[row.priority as Task['priority']] ?? row.priority }}</template>
            </el-table-column>
            <el-table-column prop="cargo_desc" label="货描" min-width="140" show-overflow-tooltip />
            <el-table-column label="重量（kg）" width="120" align="right">
              <template #default="{ row }">{{ Number(row.weight_kg).toFixed(3) }}</template>
            </el-table-column>
            <el-table-column label="体积（cm³）" width="130" align="right">
              <template #default="{ row }">{{ Number(row.volume_cm3).toFixed(2) }}</template>
            </el-table-column>
          </el-table>
          <p class="yc-dsp__hint">留在待调度：当前没有空闲车辆，或这些车装不下（含已挂任务的余量）。</p>
        </template>

        <EmptyState
          v-if="!autoResult.assigned.length && !autoResult.unassigned.length"
          title="没有可派的待调度任务"
          description="先把任务建出来，或者刷新列表看看是不是都被派走了。"
        />

        <template v-if="autoResult.trips.length">
          <h3 class="yc-dsp__sect">用到的行车任务（{{ autoResult.trips.length }}）</h3>
          <el-table :data="autoResult.trips" row-key="id">
            <el-table-column prop="doc_no" label="行车任务号" min-width="150" />
            <el-table-column label="状态" width="110">
              <template #default="{ row }">{{ TRIP_STATUS_LABELS[row.status as TripStatus] ?? row.status }}</template>
            </el-table-column>
            <el-table-column label="创建时间" min-width="155">
              <template #default="{ row }">{{ formatDateTime(row.created_at) }}</template>
            </el-table-column>
          </el-table>
        </template>
      </template>

      <template #footer>
        <el-button type="primary" @click="autoOpen = false">知道了</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-dsp {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 230px;
  }

  &__ref {
    width: 140px;
  }

  &__full {
    width: 100%;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__formrow {
    display: flex;
    flex-wrap: wrap;
    gap: var(--yc-space-3);

    .el-form-item {
      margin-bottom: var(--yc-space-3);
    }
  }

  &__hint {
    margin: var(--yc-space-1) 0 0;
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.7;
  }

  &__facts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: var(--yc-space-2) var(--yc-space-4);
    margin-bottom: var(--yc-space-4);
    padding: var(--yc-space-3);
    background: var(--yc-fill-light, #f5f7fa);
    border-radius: var(--yc-radius, 6px);
  }

  &__fact {
    font-size: var(--yc-font);

    span {
      margin-right: var(--yc-space-2);
      color: var(--yc-text-secondary);
      font-size: var(--yc-font-sm);
    }
  }

  &__summary {
    margin: 0 0 var(--yc-space-3);
    font-size: var(--yc-font);
  }

  &__sect {
    margin: var(--yc-space-4) 0 var(--yc-space-2);
    font-size: var(--yc-font);
    font-weight: 600;
  }
}
</style>
